import { useAppDispatch } from '_store';
import {
  setHeatmapActiveAction,
  setHeatmapBaseRangeAction,
  setHeatmapDateRangeAction,
  setHeatmapScaleToVisibleDataAction,
} from '_store/application/reducer';
import { heatmapSelector } from '_store/application/selectors';
import { useLocalization } from '@fluent/react';
import { addDays, endOfDay, format, getDay, startOfDay, subDays } from 'date-fns';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

import DatePicker from '../../items/DatePicker';
import type { HeatmapMetric } from '../../routes/MapView/heatmapCams';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip);

const useStyles = makeStyles((_theme) => ({
  containerBox: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 10,
    border: '1px solid #e0e0e0',
    borderStyle: 'dashed',
    borderRadius: 2,
    padding: '15px 10px',
    gap: 8,
  },
  box: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 10,
    paddingTop: 10,
  },
  switch: { fontSize: 14 },
  dateRangeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
    '& > *': { flex: '1 1 200px', minWidth: 0 },
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
  },
  selectLabel: {
    marginBottom: 4,
  },
  filterSelect: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.18)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.28)',
    },
  },
  filterInput: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.18)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.28)',
    },
  },
  presetGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 10,
  },
  presetRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  presetRowLabel: {
    width: 56,
    flexShrink: 0,
    fontSize: 12,
    color: 'rgba(0,0,0,0.6)',
  },
  presetBtn: {
    minWidth: 32,
    padding: '4px 8px',
    fontSize: 12,
  },
  customDurationRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
  },
  customDurationInput: {
    width: 100,
  },
  customUnitSelect: {
    minWidth: 100,
  },
  slotCanvas: {
    width: '100%',
    height: 12,
    borderRadius: 4,
    display: 'block',
    marginTop: 4,
  },
  slotSlider: {
    marginTop: 4,
    marginBottom: 0,
  },
  slotChart: {
    width: '100%',
    marginTop: 8,
  },
  slotChartBlock: {
    marginBottom: 6,
  },
  slotChartCanvas: {
    width: '100%',
    height: 14,
    display: 'block',
    borderRadius: 2,
  },
  slotChartLabelUnder: {
    fontSize: 10,
    marginTop: 2,
  },
  timeslotChartContainer: {
    width: '100%',
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid rgba(0,0,0,0.12)',
  },
  timeslotChart: {
    width: '100%',
    height: 280,
    position: 'relative' as const,
  },
}));

type RangeType = 'relative' | 'single' | 'absolute';
/** Day of week: all | weekdays | weekends | sun..sat */
type DayFilter =
  | 'all'
  | 'weekdays'
  | 'weekends'
  | 'sun'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat';
type Timeslot = '15m' | '1h' | '1day';

function dayFilterToDayNumber(filter: DayFilter): number | null {
  const map: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };
  return map[filter] ?? null;
}

const MS_SECOND = 1000;
const MS_MINUTE = 60 * MS_SECOND;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;
const MS_WEEK = 7 * MS_DAY;
const MS_MONTH = 30 * MS_DAY;
const MS_QUARTER_HOUR = 15 * MS_MINUTE;

/** Default granularity from interest-interval duration. */
function defaultTimeslotForDuration(durationMs: number): Timeslot {
  if (durationMs < 4 * MS_HOUR) return '15m';
  if (durationMs <= 48 * MS_HOUR) return '1h';
  return '1day';
}

type RelativeUnit = 'minute' | 'hour' | 'day' | 'week' | 'month';

function unitToMs(unit: RelativeUnit): number {
  switch (unit) {
    case 'minute':
      return MS_MINUTE;
    case 'hour':
      return MS_HOUR;
    case 'day':
      return MS_DAY;
    case 'week':
      return MS_WEEK;
    case 'month':
      return MS_MONTH;
    default:
      return MS_DAY;
  }
}

const RELATIVE_PRESETS: { unit: RelativeUnit; labelKey: string; values: number[] }[] = [
  { unit: 'minute', labelKey: 'dashboard-heatmap-unit-minutes', values: [1, 3, 5, 15, 30, 45] },
  { unit: 'hour', labelKey: 'dashboard-heatmap-unit-hours', values: [1, 2, 3, 6, 8, 12] },
  { unit: 'day', labelKey: 'dashboard-heatmap-unit-days', values: [1, 2, 3, 4, 5, 6] },
  { unit: 'week', labelKey: 'dashboard-heatmap-unit-weeks', values: [1, 2, 4, 6] },
  { unit: 'month', labelKey: 'dashboard-heatmap-unit-months', values: [3, 6, 12, 15] },
];

function isWeekday(ts: number): boolean {
  const d = getDay(new Date(ts));
  return d >= 1 && d <= 5;
}

function isWeekend(ts: number): boolean {
  const d = getDay(new Date(ts));
  return d === 0 || d === 6;
}

export const HeatmapControls: FC = () => {
  const classes = useStyles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const heatmap = useSelector(heatmapSelector);

  const timeExtentMin = heatmap.timeExtent[0];
  const timeExtentMax = heatmap.timeExtent[1];
  const dataEnd = timeExtentMax > 0 ? new Date(timeExtentMax) : new Date();
  const dataStart = timeExtentMin > 0 ? new Date(timeExtentMin) : subDays(dataEnd, 30);

  // Range type and params
  const [rangeType, setRangeType] = useState<RangeType>('absolute');
  const [relativeDuration, setRelativeDuration] = useState<number>(3);
  const [relativeUnit, setRelativeUnit] = useState<RelativeUnit>('hour');
  const [singleDate, setSingleDate] = useState<Date | null>(() =>
    timeExtentMax > 0 ? startOfDay(new Date(timeExtentMax)) : null,
  );
  const [absoluteStart, setAbsoluteStart] = useState<Date | null>(() =>
    timeExtentMin > 0 ? startOfDay(new Date(timeExtentMin)) : null,
  );
  const [absoluteEnd, setAbsoluteEnd] = useState<Date | null>(() =>
    timeExtentMax > 0 ? endOfDay(new Date(timeExtentMax)) : null,
  );

  // Day filter (day of week)
  const [dayFilter, setDayFilter] = useState<DayFilter>('all');

  // Timeslot and current window
  const [timeslot, setTimeslot] = useState<Timeslot>(() =>
    timeExtentMax > timeExtentMin
      ? defaultTimeslotForDuration(timeExtentMax - timeExtentMin)
      : '1h',
  );
  const [currentWindowIndex, setCurrentWindowIndex] = useState(0);

  // When range type changes, reset day filter
  useEffect(() => {
    setDayFilter('all');
    setCurrentWindowIndex(0);
  }, [rangeType]);

  // Compute effective base range from range type
  const effectiveBaseRange = useMemo((): [number, number] | null => {
    if (timeExtentMax <= timeExtentMin) return null;
    const teMin = timeExtentMin;
    const teMax = timeExtentMax;
    if (rangeType === 'relative') {
      const end = Math.min(teMax, new Date().getTime());
      const msAgo = relativeDuration * unitToMs(relativeUnit);
      const start = Math.max(teMin, end - msAgo);
      return [start, end];
    }
    if (rangeType === 'single') {
      if (!singleDate) return null;
      const start = startOfDay(singleDate).getTime();
      const end = endOfDay(singleDate).getTime();
      return [start, end];
    }
    if (rangeType === 'absolute') {
      if (!absoluteStart || !absoluteEnd) return null;
      const start = startOfDay(absoluteStart).getTime();
      const minEnd = endOfDay(addDays(absoluteStart, 1)).getTime(); // 2-day minimum
      let end = endOfDay(absoluteEnd).getTime();
      if (end < minEnd) end = minEnd;
      // No past limit on start; end is capped by timeExtent (e.g. today)
      return [start, Math.min(teMax, end)];
    }
    return null;
  }, [
    rangeType,
    relativeDuration,
    relativeUnit,
    singleDate,
    absoluteStart,
    absoluteEnd,
    timeExtentMin,
    timeExtentMax,
    dataEnd,
  ]);

  // Apply day filter (day of week): list of day-start timestamps
  const filteredDayStarts = useMemo((): number[] => {
    if (!effectiveBaseRange) return [];
    const [start, end] = effectiveBaseRange;
    const dayNum = dayFilterToDayNumber(dayFilter);
    const days: number[] = [];
    let d = startOfDay(new Date(start)).getTime();
    const endDay = startOfDay(new Date(end)).getTime();
    while (d <= endDay) {
      if (dayFilter === 'all') days.push(d);
      else if (dayFilter === 'weekdays' && isWeekday(d)) days.push(d);
      else if (dayFilter === 'weekends' && isWeekend(d)) days.push(d);
      else if (dayNum !== null && getDay(new Date(d)) === dayNum) days.push(d);
      d += MS_DAY;
    }
    return days;
  }, [effectiveBaseRange, dayFilter]);

  const interestDurationMs = effectiveBaseRange
    ? effectiveBaseRange[1] - effectiveBaseRange[0]
    : 0;

  // Default granularity from interest-interval duration (and when range type changes)
  useEffect(() => {
    if (interestDurationMs <= 0) return;
    setTimeslot(defaultTimeslotForDuration(interestDurationMs));
    setCurrentWindowIndex(0);
  }, [interestDurationMs, rangeType]);

  // Build list of windows [startTs, endTs][]
  const windows = useMemo((): [number, number][] => {
    if (!effectiveBaseRange || filteredDayStarts.length === 0) return [];
    if (timeslot === '1day') {
      return filteredDayStarts.map((dayStart) => [
        dayStart,
        endOfDay(new Date(dayStart)).getTime(),
      ]);
    }
    // 1h / 15m: slot windows clipped to the interest range on each filtered day
    const [baseStart, baseEnd] = effectiveBaseRange;
    const slotMs = timeslot === '1h' ? MS_HOUR : MS_QUARTER_HOUR;
    const result: [number, number][] = [];
    filteredDayStarts.forEach((dayStart) => {
      const dayEnd = endOfDay(new Date(dayStart)).getTime();
      const segmentStart = Math.max(dayStart, baseStart);
      const segmentEnd = Math.min(dayEnd, baseEnd);
      let t = segmentStart;
      while (t + slotMs <= segmentEnd + 1) {
        result.push([t, t + slotMs]);
        t += slotMs;
      }
    });
    return result;
  }, [effectiveBaseRange, filteredDayStarts, timeslot]);

  // Current window and sync to Redux (depend on primitives to avoid infinite loop)
  const currentWindow = windows[currentWindowIndex] ?? windows[0];
  const windowStart = currentWindow?.[0] ?? 0;
  const windowEnd = currentWindow?.[1] ?? 0;

  const currentWindowRangeLabel = useMemo((): string => {
    if (windowStart === 0 && windowEnd === 0) return '';
    const start = new Date(windowStart);
    const end = new Date(windowEnd);
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();
    if (sameDay) {
      return `${format(start, 'd MMM yyyy, HH:mm')} – ${format(end, 'HH:mm')}`;
    }
    return `${format(start, 'd MMM yyyy, HH:mm')} – ${format(end, 'd MMM yyyy, HH:mm')}`;
  }, [windowStart, windowEnd]);
  const baseStart = effectiveBaseRange?.[0] ?? 0;
  const baseEnd = effectiveBaseRange?.[1] ?? 0;
  useEffect(() => {
    if (baseStart >= baseEnd) return;
    dispatch(setHeatmapBaseRangeAction([baseStart, baseEnd]));
  }, [baseStart, baseEnd, dispatch]);

  useEffect(() => {
    if (windowStart !== 0 || windowEnd !== 0) {
      dispatch(setHeatmapDateRangeAction([windowStart, windowEnd]));
    }
  }, [windowStart, windowEnd, dispatch]);

  // Clamp currentWindowIndex when windows change
  useEffect(() => {
    if (windows.length > 0 && currentWindowIndex >= windows.length) {
      setCurrentWindowIndex(windows.length - 1);
    }
  }, [windows.length, currentWindowIndex]);

  const heatmapMetric: HeatmapMetric =
    heatmap.active === 'counter' || heatmap.active === 'speed_value' ? heatmap.active : 'counter';

  // Per-window: record count, average, and sum of the active metric (for canvas + chart)
  const slotStats = useMemo((): { counts: number[]; averages: number[]; sums: number[] } => {
    const data = heatmap.slotData ?? [];
    if (data.length === 0 || windows.length === 0)
      return { counts: [], averages: [], sums: [] };
    const getValue = (r: { counter: number; speedValue: number }) =>
      heatmapMetric === 'counter' ? r.counter : r.speedValue;
    const counts: number[] = [];
    const averages: number[] = [];
    const sums: number[] = [];
    for (const [minT, maxT] of windows) {
      const inRange = data.filter((r) => r.createdAt >= minT && r.createdAt <= maxT);
      counts.push(inRange.length);
      if (inRange.length === 0) {
        averages.push(0);
        sums.push(0);
      } else {
        const sum = inRange.reduce((s, r) => s + getValue(r), 0);
        sums.push(sum);
        averages.push(sum / inRange.length);
      }
    }
    return { counts, averages, sums };
  }, [heatmap.slotData, heatmapMetric, windows]);

  const slotAverages = slotStats.averages;

  // Chart.js data for the current timeslot: 15m buckets for 15m/1h windows, 1h for day windows
  // counter = sum of cell values in bucket; speed_value = average (sum is not meaningful for speed)
  const currentWindowChartData = useMemo(() => {
    const data = heatmap.slotData ?? [];
    const empty = {
      labels: [] as string[],
      datasets: [{ label: heatmapMetric, data: [] as number[], backgroundColor: 'rgba(33, 150, 243, 0.6)' }],
    };
    if (windowStart === 0 || windowEnd === 0) return empty;
    const bucketMs = timeslot === '1day' ? MS_HOUR : MS_QUARTER_HOUR;
    const labels: string[] = [];
    const bucketStarts: number[] = [];
    let t = Math.floor(windowStart / bucketMs) * bucketMs;
    while (t < windowEnd) {
      bucketStarts.push(t);
      labels.push(format(new Date(t), 'HH:mm'));
      t += bucketMs;
    }
    if (labels.length === 0) return empty;
    if (data.length === 0) {
      return {
        labels,
        datasets: [{ label: heatmapMetric, data: labels.map(() => 0), backgroundColor: 'rgba(33, 150, 243, 0.6)' }],
      };
    }
    const getValue = (r: { counter: number; speedValue: number }) =>
      heatmapMetric === 'counter' ? r.counter : r.speedValue;
    const aggregateBucket = (rows: typeof data): number => {
      if (rows.length === 0) return 0;
      const sum = rows.reduce((s, r) => s + getValue(r), 0);
      return heatmapMetric === 'speed_value' ? sum / rows.length : sum;
    };
    const inWindow = data.filter((r) => r.createdAt >= windowStart && r.createdAt <= windowEnd);
    const values = bucketStarts.map((bucketStart, i) => {
      const bucketEnd = i === bucketStarts.length - 1 ? windowEnd : bucketStart + bucketMs;
      const isLast = i === bucketStarts.length - 1;
      const inBucket = inWindow.filter(
        (r) => r.createdAt >= bucketStart && (isLast ? r.createdAt <= bucketEnd : r.createdAt < bucketEnd),
      );
      return aggregateBucket(inBucket);
    });
    return {
      labels,
      datasets: [{ label: heatmapMetric, data: values, backgroundColor: 'rgba(33, 150, 243, 0.6)' }],
    };
  }, [heatmap.slotData, heatmapMetric, windowStart, windowEnd, timeslot]);

  // Y-axis max: full interest range (all windows) by default; current window when scaleToVisibleData
  const timeslotChartYMax = useMemo((): number | undefined => {
    const data = heatmap.slotData ?? [];
    if (windows.length === 0) return undefined;
    const bucketMs = timeslot === '1day' ? MS_HOUR : MS_QUARTER_HOUR;
    const getValue = (r: { counter: number; speedValue: number }) =>
      heatmapMetric === 'counter' ? r.counter : r.speedValue;
    const aggregateBucket = (rows: typeof data): number => {
      if (rows.length === 0) return 0;
      const sum = rows.reduce((s, r) => s + getValue(r), 0);
      return heatmapMetric === 'speed_value' ? sum / rows.length : sum;
    };

    const maxBucketInWindow = (winStart: number, winEnd: number): number => {
      if (winStart === 0 && winEnd === 0) return 0;
      const inWindow = data.filter((r) => r.createdAt >= winStart && r.createdAt <= winEnd);
      let max = 0;
      let t = Math.floor(winStart / bucketMs) * bucketMs;
      const bucketStarts: number[] = [];
      while (t < winEnd) {
        bucketStarts.push(t);
        t += bucketMs;
      }
      bucketStarts.forEach((bucketStart, idx) => {
        const bucketEnd = idx === bucketStarts.length - 1 ? winEnd : bucketStart + bucketMs;
        const isLast = idx === bucketStarts.length - 1;
        const inBucket = inWindow.filter(
          (r) =>
            r.createdAt >= bucketStart &&
            (isLast ? r.createdAt <= bucketEnd : r.createdAt < bucketEnd),
        );
        const value = aggregateBucket(inBucket);
        if (value > max) max = value;
      });
      return max;
    };

    if (heatmap.scaleToVisibleData) {
      const visibleMax = maxBucketInWindow(windowStart, windowEnd);
      return visibleMax > 0 ? visibleMax : undefined;
    }
    let globalMax = 0;
    windows.forEach(([wStart, wEnd]) => {
      const m = maxBucketInWindow(wStart, wEnd);
      if (m > globalMax) globalMax = m;
    });
    return globalMax > 0 ? globalMax : undefined;
  }, [
    heatmap.slotData,
    heatmapMetric,
    heatmap.scaleToVisibleData,
    windows,
    timeslot,
    windowStart,
    windowEnd,
  ]);

  // Memoized options so Chart.js only updates data when slider moves, not full reload
  const timeslotChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { mode: 'index' as const },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: l10n.getString('dashboard-heatmap-timeslot-chart-x') ?? 'Time',
          },
          ticks: { maxRotation: 45 },
        },
        y: {
          beginAtZero: true,
          ...(timeslotChartYMax != null ? { max: timeslotChartYMax } : {}),
          title: {
            display: true,
            text:
              heatmapMetric === 'counter'
                ? (l10n.getString('dashboard-heatmap-timeslot-chart-y-count') ?? 'Count')
                : (l10n.getString('dashboard-heatmap-timeslot-chart-y-value') ?? 'Value'),
          },
        },
      },
    }),
    [heatmapMetric, l10n, timeslotChartYMax],
  );

  // Heatmap gradient: blue (low) -> lime (mid) -> red (high), matching MapView heat layer
  const heatmapGradientRgb = useCallback((t: number): [number, number, number] => {
    const blue: [number, number, number] = [0, 0, 255];
    const lime: [number, number, number] = [0, 255, 0];
    const red: [number, number, number] = [255, 0, 0];
    const lerp = (a: [number, number, number], b: [number, number, number], u: number) =>
      [Math.round(a[0] + (b[0] - a[0]) * u), Math.round(a[1] + (b[1] - a[1]) * u), Math.round(a[2] + (b[2] - a[2]) * u)] as [number, number, number];
    if (t <= 0.4) return blue;
    if (t <= 0.65) return lerp(blue, lime, (t - 0.4) / 0.25);
    return lerp(lime, red, (t - 0.65) / 0.35);
  }, []);

  const slotCanvasRef = useRef<HTMLCanvasElement>(null);
  const recordsChartRef = useRef<HTMLCanvasElement>(null);
  const averageChartRef = useRef<HTMLCanvasElement>(null);
  const sumChartRef = useRef<HTMLCanvasElement>(null);

  const drawBarChart = useCallback(
    (canvasEl: HTMLCanvasElement | null, data: number[], fillStyle: string) => {
      if (!canvasEl || data.length === 0) return;
      const dpr = window.devicePixelRatio ?? 1;
      const w = canvasEl.clientWidth;
      const h = canvasEl.clientHeight;
      const c = canvasEl;
      c.width = w * dpr;
      c.height = h * dpr;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      const max = Math.max(...data, 1);
      const barW = (w - (data.length - 1)) / data.length;
      data.forEach((v, i) => {
        const barH = Math.max(2, (v / max) * h);
        ctx.fillStyle = fillStyle;
        ctx.fillRect(i * (barW + 1), h - barH, barW + 0.5, barH);
      });
    },
    [],
  );

  useLayoutEffect(() => {
    const canvas = slotCanvasRef.current;
    if (!canvas || slotAverages.length === 0) return;
    const dpr = window.devicePixelRatio ?? 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const c = canvas;
    c.width = w * dpr;
    c.height = h * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const max = Math.max(...slotAverages, 1);
    const n = slotAverages.length;
    const gradient = ctx.createLinearGradient(0, 0, w, 0);
    slotAverages.forEach((val, i) => {
      const t = val / max;
      const [r, g, b] = heatmapGradientRgb(t);
      const color = `rgb(${r},${g},${b})`;
      if (n === 1) {
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, color);
      } else {
        gradient.addColorStop(i / (n - 1), color);
      }
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  }, [slotAverages, heatmapGradientRgb]);

  useLayoutEffect(() => {
    drawBarChart(recordsChartRef.current, slotStats.counts, 'rgba(33, 150, 243, 0.7)');
  }, [slotStats.counts, drawBarChart]);

  useLayoutEffect(() => {
    drawBarChart(averageChartRef.current, slotStats.averages, 'rgba(76, 175, 80, 0.7)');
  }, [slotStats.averages, drawBarChart]);

  useLayoutEffect(() => {
    drawBarChart(sumChartRef.current, slotStats.sums, 'rgba(255, 152, 0, 0.7)');
  }, [slotStats.sums, drawBarChart]);

  const timeslotOptions: { value: Timeslot; labelKey: string }[] = [
    { value: '15m', labelKey: 'dashboard-heatmap-timeslot-15m' },
    { value: '1h', labelKey: 'dashboard-heatmap-timeslot-1h' },
    { value: '1day', labelKey: 'dashboard-heatmap-timeslot-1day' },
  ];

  const handleAbsoluteStartChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      setAbsoluteStart(startOfDay(date));
      if (absoluteEnd && endOfDay(date).getTime() >= absoluteEnd.getTime()) {
        setAbsoluteEnd(endOfDay(addDays(date, 1)));
      }
    },
    [absoluteEnd],
  );

  const handleAbsoluteEndChange = useCallback(
    (date: Date | null) => {
      if (!date) return;
      const end = endOfDay(date);
      if (absoluteStart) {
        const minEnd = addDays(absoluteStart, 2);
        if (end.getTime() < startOfDay(minEnd).getTime()) {
          setAbsoluteEnd(endOfDay(addDays(absoluteStart, 1)));
          return;
        }
      }
      setAbsoluteEnd(end);
    },
    [absoluteStart],
  );

  if (heatmap.timeExtent[0] >= heatmap.timeExtent[1]) {
    return (
      <Box p={2}>
        <Typography color="textSecondary">
          {l10n.getString('dashboard-heatmap-no-data') ?? 'No heatmap data loaded.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.containerBox} display="flex">
      <Box className={classes.box} display="flex" flexDirection="column" alignItems="stretch">
        <Box className={classes.box}>
          <Typography variant="body2" className={classes.switch}>
            {l10n.getString('forms-settings-heatmap-visibility') ?? 'Heatmap visibility'}
          </Typography>
          <Select
            className={classes.filterSelect}
            value={heatmap.active}
            onChange={(e) =>
              dispatch(
                setHeatmapActiveAction(
                  e.target.value as 'inactive' | 'counter' | 'speed_value',
                ),
              )
            }
            variant="outlined"
            style={{ minWidth: 220 }}
          >
            <MenuItem value="counter">
              {l10n.getString('forms-settings-heatmap-show-vehicle-count')
                ?? 'Show vehicle count heatmap'}
            </MenuItem>
            <MenuItem value="speed_value">
              {l10n.getString('forms-settings-heatmap-show-speed-value')
                ?? 'Show speed value heatmap'}
            </MenuItem>
          </Select>
        </Box>
        {heatmap.active !== 'inactive' && (
          <FormControlLabel
            style={{ marginTop: 4, marginLeft: 10 }}
            control={
              <Switch
                checked={heatmap.scaleToVisibleData}
                onChange={(e) => dispatch(setHeatmapScaleToVisibleDataAction(e.target.checked))}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {l10n.getString('forms-settings-heatmap-scale-to-visible')
                  ?? 'Scale colors to visible data'}
              </Typography>
            }
          />
        )}
        {heatmap.active !== 'inactive' && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            style={{ marginTop: 8, width: '100%' }}
          >
            {/* Range type */}
            <Typography variant="body2" color="textSecondary" className={classes.selectLabel}>
              {l10n.getString('dashboard-heatmap-range-type') ?? 'Range type'}
            </Typography>
            <Select
              className={classes.filterSelect}
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value as RangeType)}
              variant="outlined"
              style={{ width: '100%', marginBottom: 12 }}
            >
              <MenuItem value="relative">
                {l10n.getString('dashboard-heatmap-range-relative') ?? 'Relative date range'}
              </MenuItem>
              <MenuItem value="single">
                {l10n.getString('dashboard-heatmap-range-single') ?? 'Single day'}
              </MenuItem>
              <MenuItem value="absolute">
                {l10n.getString('dashboard-heatmap-range-absolute') ?? 'Absolute date range'}
              </MenuItem>
            </Select>

            {rangeType === 'relative' && (
              <Box marginBottom={2}>
                <Typography variant="body2" color="primary" style={{ marginBottom: 8 }}>
                  {l10n.getString('dashboard-heatmap-relative-range-summary', {
                    duration: relativeDuration,
                    unit:
                      relativeDuration === 1
                        ? (l10n.getString(`dashboard-heatmap-unit-${relativeUnit}`) ?? relativeUnit)
                        : (l10n.getString(`dashboard-heatmap-unit-${relativeUnit}s`) ?? relativeUnit + 's'),
                  }) ?? `Last ${relativeDuration} ${relativeUnit}${relativeDuration === 1 ? '' : 's'}`}
                </Typography>
                <Box className={classes.presetGrid}>
                  {RELATIVE_PRESETS.map(({ unit, labelKey, values }) => (
                    <Box key={unit} className={classes.presetRow}>
                      <Typography variant="body2" className={classes.presetRowLabel}>
                        {l10n.getString(labelKey) ?? unit}
                      </Typography>
                      {values.map((val) => {
                        const selected =
                          relativeUnit === unit && relativeDuration === val;
                        return (
                          <Button
                            key={val}
                            size="small"
                            variant={selected ? 'contained' : 'outlined'}
                            color={selected ? 'primary' : 'default'}
                            className={classes.presetBtn}
                            onClick={() => {
                              setRelativeDuration(val);
                              setRelativeUnit(unit);
                            }}
                          >
                            {val}
                          </Button>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
                <Typography variant="body2" color="textSecondary" className={classes.selectLabel} style={{ marginTop: 4 }}>
                  {l10n.getString('dashboard-heatmap-custom-duration') ?? 'Duration'} / {l10n.getString('dashboard-heatmap-unit-of-time') ?? 'Unit of time'}
                </Typography>
                <Box className={classes.customDurationRow}>
                  <TextField
                    type="number"
                    size="small"
                    placeholder={l10n.getString('dashboard-heatmap-custom-duration-placeholder') ?? 'Enter duration'}
                    value={relativeDuration}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n) && n >= 1) setRelativeDuration(n);
                    }}
                    inputProps={{ min: 1 }}
                    variant="outlined"
                    className={`${classes.filterInput} ${classes.customDurationInput}`}
                  />
                  <Select
                    className={`${classes.filterSelect} ${classes.customUnitSelect}`}
                    value={relativeUnit}
                    onChange={(e) => setRelativeUnit(e.target.value as RelativeUnit)}
                    variant="outlined"
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="minute">
                      {l10n.getString('dashboard-heatmap-unit-minute') ?? 'minute'}
                    </MenuItem>
                    <MenuItem value="hour">
                      {l10n.getString('dashboard-heatmap-unit-hour') ?? 'hour'}
                    </MenuItem>
                    <MenuItem value="day">
                      {l10n.getString('dashboard-heatmap-unit-day') ?? 'day'}
                    </MenuItem>
                    <MenuItem value="week">
                      {l10n.getString('dashboard-heatmap-unit-week') ?? 'week'}
                    </MenuItem>
                    <MenuItem value="month">
                      {l10n.getString('dashboard-heatmap-unit-month') ?? 'month'}
                    </MenuItem>
                  </Select>
                </Box>
              </Box>
            )}

            {rangeType === 'single' && (
              <Box marginBottom={2} className={classes.dateRangeRow}>
                <DatePicker
                  label={l10n.getString('dashboard-heatmap-single-day') ?? 'Day'}
                  value={singleDate}
                  onChange={(d) => setSingleDate(d ? startOfDay(d) : null)}
                  maxDate={endOfDay(new Date())}
                  disablePast={false}
                  style={{ marginTop: 0, width: '100%' }}
                />
              </Box>
            )}

            {rangeType === 'absolute' && (
              <Box marginBottom={2} className={classes.dateRangeRow}>
                <DatePicker
                  label={l10n.getString('dashboard-heatmap-start-date') ?? 'Start date'}
                  value={absoluteStart}
                  onChange={handleAbsoluteStartChange}
                  maxDate={
                    absoluteEnd
                      ? subDays(absoluteEnd, 1)
                      : dataEnd
                  }
                  disablePast={false}
                  style={{ marginTop: 0, width: '100%' }}
                />
                <DatePicker
                  label={l10n.getString('dashboard-heatmap-end-date') ?? 'End date'}
                  value={absoluteEnd}
                  onChange={handleAbsoluteEndChange}
                  minDate={
                    absoluteStart
                      ? addDays(absoluteStart, 1)
                      : dataStart
                  }
                  maxDate={dataEnd}
                  disablePast={false}
                  style={{ marginTop: 0, width: '100%' }}
                />
              </Box>
            )}

            {/* Day filter (day of week): only when range spans multiple days */}
            {rangeType !== 'single' && (
              <>
                <Typography variant="body2" color="textSecondary" className={classes.selectLabel}>
                  {l10n.getString('dashboard-heatmap-day-filter') ?? 'Day of week'}
                </Typography>
                <Select
                  className={classes.filterSelect}
                  value={dayFilter}
                  onChange={(e) => setDayFilter(e.target.value as DayFilter)}
                  variant="outlined"
                  style={{ width: '100%', marginBottom: 12 }}
                >
                  <MenuItem value="all">
                    {l10n.getString('dashboard-heatmap-days-all') ?? 'All days'}
                  </MenuItem>
                  <MenuItem value="weekdays">
                    {l10n.getString('dashboard-heatmap-days-weekdays') ?? 'Weekdays only'}
                  </MenuItem>
                  <MenuItem value="weekends">
                    {l10n.getString('dashboard-heatmap-days-weekends') ?? 'Weekends only'}
                  </MenuItem>
                  <MenuItem value="sun">
                    {l10n.getString('dashboard-heatmap-day-sun') ?? 'Sunday'}
                  </MenuItem>
                  <MenuItem value="mon">
                    {l10n.getString('dashboard-heatmap-day-mon') ?? 'Monday'}
                  </MenuItem>
                  <MenuItem value="tue">
                    {l10n.getString('dashboard-heatmap-day-tue') ?? 'Tuesday'}
                  </MenuItem>
                  <MenuItem value="wed">
                    {l10n.getString('dashboard-heatmap-day-wed') ?? 'Wednesday'}
                  </MenuItem>
                  <MenuItem value="thu">
                    {l10n.getString('dashboard-heatmap-day-thu') ?? 'Thursday'}
                  </MenuItem>
                  <MenuItem value="fri">
                    {l10n.getString('dashboard-heatmap-day-fri') ?? 'Friday'}
                  </MenuItem>
                  <MenuItem value="sat">
                    {l10n.getString('dashboard-heatmap-day-sat') ?? 'Saturday'}
                  </MenuItem>
                </Select>
              </>
            )}

            {/* Timeslot */}
            <Typography variant="body2" color="textSecondary" className={classes.selectLabel}>
              {l10n.getString('dashboard-heatmap-timeslot') ?? 'Time slot'}
            </Typography>
            <Select
              className={classes.filterSelect}
              value={timeslot}
              onChange={(e) => {
                setTimeslot(e.target.value as Timeslot);
                setCurrentWindowIndex(0);
              }}
              variant="outlined"
              style={{ width: '100%', marginBottom: 12 }}
            >
              {timeslotOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {l10n.getString(opt.labelKey) ?? opt.value}
                </MenuItem>
              ))}
            </Select>

            {/* Current timeslot datetime range */}
            {currentWindowRangeLabel && (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                {currentWindowRangeLabel}
              </Typography>
            )}

            {/* Timeslot slider + canvas + bar charts when we have slot data and at least one window */}
            {heatmap.slotData &&
              heatmap.slotData.length > 0 &&
              windows.length > 0 && (
              <Box marginBottom={1} width="100%">
                {windows.length > 1 && (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      {l10n.getString('dashboard-heatmap-slot') ?? 'Slot'}{' '}
                      {currentWindowIndex + 1} / {windows.length}
                    </Typography>
                    <Box
                      className={classes.slotSlider}
                      style={{
                        marginLeft: `${50 / windows.length}%`,
                        marginRight: `${50 / windows.length}%`,
                      }}
                    >
                      <Slider
                        value={currentWindowIndex}
                        min={0}
                        max={Math.max(0, windows.length - 1)}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => v + 1}
                        onChange={(_, value) => setCurrentWindowIndex(value as number)}
                      />
                    </Box>
                  </>
                )}
                {slotAverages.length > 0 && (
                  <>
                    <canvas
                      ref={slotCanvasRef}
                      className={classes.slotCanvas}
                      style={{ width: '100%', height: 6, marginBottom: 20 }}
                      aria-label={
                        l10n.getString('dashboard-heatmap-slot-canvas-label') ?? 'Data intensity per time slot'
                      }
                    />
                    <Box className={classes.slotChart}>
                      <Box className={classes.slotChartBlock}>
                        <canvas
                          ref={recordsChartRef}
                          className={classes.slotChartCanvas}
                          style={{ width: '100%', height: 14 }}
                          aria-label={l10n.getString('dashboard-heatmap-slot-records') ?? 'Records'}
                        />
                        <Typography
                          variant="caption"
                          className={classes.slotChartLabelUnder}
                          style={{ color: '#2196f3' }}
                        >
                          {l10n.getString('dashboard-heatmap-slot-records') ?? 'Records'}
                        </Typography>
                      </Box>
                      <Box className={classes.slotChartBlock}>
                        <canvas
                          ref={averageChartRef}
                          className={classes.slotChartCanvas}
                          style={{ width: '100%', height: 14 }}
                          aria-label={l10n.getString('dashboard-heatmap-slot-average') ?? 'Average'}
                        />
                        <Typography
                          variant="caption"
                          className={classes.slotChartLabelUnder}
                          style={{ color: '#4caf50' }}
                        >
                          {l10n.getString('dashboard-heatmap-slot-average') ?? 'Average'}
                        </Typography>
                      </Box>
                      {heatmapMetric === 'counter' && (
                        <Box className={classes.slotChartBlock}>
                          <canvas
                            ref={sumChartRef}
                            className={classes.slotChartCanvas}
                            style={{ width: '100%', height: 14 }}
                            aria-label={l10n.getString('dashboard-heatmap-slot-sum') ?? 'Sum'}
                          />
                          <Typography
                            variant="caption"
                            className={classes.slotChartLabelUnder}
                            style={{ color: '#ff9800' }}
                          >
                            {l10n.getString('dashboard-heatmap-slot-sum') ?? 'Sum'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Chart.js chart: selected timeslot (memoized options so only data updates) */}
            {heatmap.slotData && heatmap.slotData.length > 0 && (
              <Box className={classes.timeslotChartContainer}>
                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {l10n.getString('dashboard-heatmap-timeslot-chart-title') ?? 'Selected timeslot'}
                </Typography>
                <Box className={classes.timeslotChart}>
                  <Bar data={currentWindowChartData} options={timeslotChartOptions} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
